import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import {
  XMarkIcon,
  BellIcon as BellOutlineIcon,
  BellSlashIcon,
  ArrowUpOnSquareIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Logo from "./Logo";
import { publicNavigation, userNavigation } from "../consts";
import { useAuth } from "../features/auth/hooks/useAuth";
import { LOGIN_ROUTE, SIGNUP_ROUTE, EXTRA_ROUTE } from "../routes";
import useGetProfile from "../features/user/hooks/useGetProfile";
import eventBus from "../lib/eventBus";
import NavigationLinks from "./NavigationLinks";
import { useSubscription } from "../features/subscription/hooks/useSubscriptionData";
import { StarIcon } from "@heroicons/react/24/solid";
import { useIOSNotification } from "../hooks/useIOSNotification";
import { useFirebaseMessaging } from "../hooks/useFirebaseMessaging";
import { useToast } from "../context/ToastContext";
import { isIos, isInStandaloneMode, isAndroid } from "../services/mobileUtils";
import { FAQ_ROUTE } from "../routes";

// Helper to determine if user has premium features
// Now using the server-computed isPremium field from subscription
const usePremiumStatus = () => {
  const { subscription } = useSubscription();
  
  // Use the server-computed isPremium field based on plan cost
  const isPremium = Boolean(subscription?.isPremium);
  
  return { isPremium };
};

export default function Header() {
  const { isAuthenticated, hasRole } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Keep a ref to the header for potential measurements/behavior
  const headerRef = useRef<HTMLElement | null>(null);

  return (
    <header ref={headerRef} className="bg-white">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-7xl items-center justify-between gap-x-4 p-4 lg:px-8"
      >
        <div className="flex lg:flex-1">
          <a href="/" className="-m-1.5 p-1.5">
            <Logo />
          </a>
        </div>
        {/* Desktop */}
        <div className="hidden lg:flex lg:gap-x-12">
          <NavigationLinks
            items={publicNavigation}
            isAuthenticated={isAuthenticated}
            hasRole={hasRole}
            linkClassName="text-lg font-semibold leading-6 text-gray-900"
          />
        </div>

        <div className="flex items-center justify-end gap-x-3 whitespace-nowrap">
          {isAuthenticated ? (
            <div className="flex items-center gap-x-2 whitespace-nowrap">
              <UserMenu />
            </div>
          ) : (
            <>
              <a
                href={LOGIN_ROUTE}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
              >
                Accedi
              </a>
              <a
                href={SIGNUP_ROUTE}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Iscriviti
              </a>
            </>
          )}
        </div>
      </nav>
      
      {/* Mobile menu is now replaced by ExtraPage - keep for desktop/tablet if needed */}
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        <div className="fixed inset-0 z-10" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-4 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center gap-x-6 pr-0">
            <a href="/" className="-m-1.5 p-1.5">
              <Logo />
            </a>

            <div className="flex-1" />

            {isAuthenticated ? (
              // Keep INVITA + avatar inline and non-shrinking so they stay just before the close button
              <div className="flex items-center gap-x-2 whitespace-nowrap flex-shrink-0">
                <UserMenu />
              </div>
            ) : (
              <a
                href={SIGNUP_ROUTE}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Iscriviti
              </a>
            )}

            <button
              type="button"
              onClick={() => {}}
              className="-m-1 rounded-md p-1 text-gray-700 -ml-3 flex-shrink-0"
              aria-label="Close menu"
            >
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root" style={{ paddingBottom: "100px" }}>
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                <NavigationLinks
                  items={publicNavigation}
                  isAuthenticated={isAuthenticated}
                  hasRole={hasRole}
                  linkClassName="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                />
              </div>
              {!isAuthenticated && (
                <div className="py-6">
                  <a
                    href={LOGIN_ROUTE}
                    className="block w-full rounded-lg px-4 py-2.5 text-base font-semibold text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 text-center transition-colors duration-200"
                  >
                    Accedi
                  </a>
                </div>
              )}

              {isAuthenticated &&
                userNavigation.map((item, index) => {
                  // Check if the item should be displayed based on roles
                  const shouldShow =
                    !item.roles || item.roles.some((role) => hasRole(role));

                  if (!shouldShow) return null;

                  return (
                    <div key={`mobile-${index}`} className="py-6">
                      <a href={item.href}>{item.name}</a>
                    </div>
                  );
                })}
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}

function UserMenu({ refetchWallet }: { refetchWallet?: any }) {
  const { user, loading } = useGetProfile();
  const { hasRole } = useAuth();

  // Check notification status
  const { needsInstallation } = useIOSNotification();
  const { requestPushPermission } = useFirebaseMessaging();
  const { showToast, hideToast } = useToast();
  const [permissionState, setPermissionState] = useState<NotificationPermission | "default">("default");
  
  // Update permission state
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setPermissionState(Notification.permission);
    }
  }, []);
  
  // Determine if we should show the notification icon
  const shouldShowNotificationIcon = React.useMemo(() => {
    const iosCheck = isIos() && needsInstallation;
    const androidCheck = (!isIos() || (isIos() && isInStandaloneMode())) && 
        typeof window !== "undefined" && 
        "Notification" in window && 
        permissionState !== "granted";
    
    const result = iosCheck || androidCheck;
    
    console.log('Notification icon check:', {
      isIos: isIos(),
      needsInstallation,
      isInStandaloneMode: isInStandaloneMode(),
      isAndroid: isAndroid(),
      hasNotificationAPI: typeof window !== "undefined" && "Notification" in window,
      permissionState,
      iosCheck,
      androidCheck,
      shouldShow: result
    });
    
    return result;
  }, [needsInstallation, permissionState]);
  
  // Handle notification icon click
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNotificationClick = React.useCallback(() => {
    // Clear any existing toast dismissal timestamps to show immediately
    localStorage.removeItem("ios_notification_toast_dismissed_until");
    localStorage.removeItem("notification_toast_dismissed_until");
    
    // iOS case - show installation toast
    if (isIos() && needsInstallation) {
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <ArrowUpOnSquareIcon className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-gray-900 mb-1">
              Attiva le notifiche
            </h3>
            <p className="text-sm text-gray-700">
              Premi <ArrowUpOnSquareIcon className="inline h-5 w-5 mx-0.5 align-text-bottom" /> dalla barra inferiore
              e seleziona "Aggiungi a Home" per attivarle
            </p>
          </div>
          <button
            onClick={() => {
              const dismissUntil = Date.now() + (30 * 60 * 1000);
              localStorage.setItem("ios_notification_toast_dismissed_until", dismissUntil.toString());
              hideToast();
            }}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true }
      );
      return;
    }
    
    // Android/Chrome case
    if (permissionState === "denied" && isAndroid()) {
      // Show denied toast
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-gray-900 mb-1">
              Notifiche bloccate
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              Hai bloccato le notifiche per GioJoy. Se è quello che desideri,
              puoi chiudere questo messaggio.
            </p>
            <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded mb-3">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Vuoi riattivare le notifiche?
              </p>
              <a
                className="inline-flex items-center justify-center gap-1.5 bg-red-500 text-white font-semibold px-4 py-2 rounded-lg shadow-sm hover:bg-red-600 transition-colors text-sm"
                href={FAQ_ROUTE + "#notifiche-android"}
                onClick={() => {
                  localStorage.setItem("notification_instructions_viewed", "true");
                  setTimeout(() => {
                    localStorage.removeItem("notification_instructions_viewed");
                  }, 60 * 60 * 1000);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                Segui le istruzioni
              </a>
            </div>
          </div>
          <button
            onClick={() => {
              const dismissUntil = Date.now() + (30 * 60 * 1000);
              localStorage.setItem("notification_toast_dismissed_until", dismissUntil.toString());
              hideToast();
            }}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi per 30 minuti"
            title="Nascondi questo messaggio per 30 minuti"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true }
      );
    } else if (permissionState === "default") {
      // Show request toast
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <BellOutlineIcon className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-gray-900 mb-1">
              Attiva le notifiche
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Resta in contatto con gli organizzatori e gli altri partecipanti degli eventi.
            </p>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const result = await requestPushPermission();
                  console.log("Permission result:", result);
                  setPermissionState(Notification.permission);
                  if (Notification.permission !== "denied") {
                    hideToast();
                  } else {
                    const dismissUntil = Date.now() + (30 * 60 * 1000);
                    localStorage.setItem("notification_toast_dismissed_until", dismissUntil.toString());
                    hideToast();
                  }
                }}
                className="flex-1 bg-blue-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm shadow-sm"
              >
                Accetto
              </button>
              <button
                onClick={() => {
                  const dismissUntil = Date.now() + (30 * 60 * 1000);
                  localStorage.setItem("notification_toast_dismissed_until", dismissUntil.toString());
                  hideToast();
                }}
                className="flex-1 bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            onClick={() => {
              const dismissUntil = Date.now() + (30 * 60 * 1000);
              localStorage.setItem("notification_toast_dismissed_until", dismissUntil.toString());
              hideToast();
            }}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi per 30 minuti"
            title="Nascondi questo messaggio per 30 minuti"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true }
      );
    }
  }, [needsInstallation, permissionState, requestPushPermission, showToast, hideToast]);

  useEffect(() => {
    const off = eventBus.on('wallet:updated', (payload) => {
      try {
        // prefer a light refetch to keep cache consistent; if caller wants, they can implement direct cache writes
        if (refetchWallet) refetchWallet();
      } catch (e) {
        // ignore
      }
    });
    return () => off();
  }, [refetchWallet]);
  const { isPremium } = usePremiumStatus();

  if (loading) {
    return null;
  }

  const baseUrl = `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? `:${window.location.port}` : ""
  }`;

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Non sei ancora iscritto a GioJoy? Iscriviti ora!`,
          url: `${baseUrl}${SIGNUP_ROUTE}?ic=${user?.referralCode}`,
        });
      } catch (error) {
        console.error("Error sharing content:", error);
      }
    } else {
      console.error("Web Share API not supported");
    }
  };

  console.log('UserMenu render - shouldShowNotificationIcon:', shouldShowNotificationIcon);

  return (
    <>
      {shouldShowNotificationIcon && (
        <button
          onClick={handleNotificationClick}
          className="relative flex items-center justify-center p-1.5 rounded-full bg-[#FF7F00]/15 ring-1 ring-[#FF7F00]/40 hover:bg-[#FF7F00]/25 transition-colors transform-gpu scale-100 hover:scale-105 flex-shrink-0"
          aria-label="Attiva notifiche"
          title="Attiva notifiche"
        >
          <span
            aria-hidden="true"
            className="absolute -inset-0.5 rounded-full bg-[#FF7F00]/25 animate-pulse"
          />
          <BellSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF7F00]" />
        </button>
      )}
      <Button
        className="flex items-center bg-green-500 border-gray-100 border rounded-lg text-white whitespace-nowrap px-3 py-1.5 text-sm sm:px-4 sm:py-2 sm:text-base flex-shrink-0"
        onClick={handleShare}
      >
        <span className="font-bold text-white"> INVITA </span>
      </Button>
      
      {/* Desktop: Dropdown Menu */}
      <div className="relative hidden lg:block flex-shrink-0">
        <Menu>
          <MenuButton className="-m-0.5 flex items-center p-0.5 relative">
            <span className="sr-only">Apri menu utente</span>
            <div className="relative">
            <img
              className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-50 ${
                isPremium 
                  ? 'ring-2 ring-gradient-to-r from-yellow-400 to-yellow-600 ring-offset-2' 
                  : ''
              }`}
              src={user?.profileImage}
              alt=""
              style={isPremium ? {
                boxShadow: '0 0 0 2px #fff, 0 0 0 4px #f59e0b, 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              } : {}}
            />
            {isPremium && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full p-0.5">
                <StarIcon className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <span className="hidden lg:flex lg:items-center">
            <span
              className="ml-4 text-sm font-semibold leading-6 text-gray-900"
              aria-hidden="true"
            >
              {user?.firstName}
            </span>
            <ChevronDownIcon
              aria-hidden="true"
              className="ml-2 h-5 w-5 text-gray-400"
            />
          </span>
        </MenuButton>
        <MenuItems
          modal={false}
          anchor="bottom end"
          className="w-56 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none [--anchor-gap:8px] z-50"
        >
          {/* User Navigation Items */}
          {userNavigation.map((item, index) => {
            const shouldShow =
              !item.roles || item.roles.some((role) => hasRole(role));

            if (!shouldShow) return null;

            return (
              <MenuItem key={`desktop-menu-${index}`}>
                <a
                  href={item.href}
                  className="block px-3 py-2 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50"
                >
                  {item.name}
                </a>
              </MenuItem>
            );
          })}
        </MenuItems>
      </Menu>
      </div>
      
      {/* Mobile: Link to ExtraPage */}
      <a href={EXTRA_ROUTE} className="-m-0.5 flex items-center p-0.5 flex-shrink-0 lg:hidden">
        <span className="sr-only">Vai a menu extra</span>
        <div className="relative">
          <img
            className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-50 ${
              isPremium 
                ? 'ring-2 ring-gradient-to-r from-yellow-400 to-yellow-600 ring-offset-2' 
                : ''
            }`}
            src={user?.profileImage}
            alt=""
            style={isPremium ? {
              boxShadow: '0 0 0 2px #fff, 0 0 0 4px #f59e0b, 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            } : {}}
          />
          {isPremium && (
            <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full p-0.5">
              <StarIcon className="h-3 w-3 text-white" />
            </div>
          )}
        </div>
        <span className="hidden lg:flex lg:items-center">
          <span
            className="ml-4 text-sm font-semibold leading-6 text-gray-900"
            aria-hidden="true"
          >
            {user?.firstName}
          </span>
        </span>
      </a>
    </>
  );
}
